import { Injectable } from '@angular/core';
import { SecureStorageEcho, SecureStorageEchoObject } from '@ionic-native/secure-storage-echo/ngx';
import { Platform } from './platform.service';
import { Logger } from './logger.service';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private storagePromise: Promise<SecureStorageEchoObject | null> | null = null;
  private useFallbackLocalStorage = false;
  private fallbackMemory: Record<string, string> = {};

  protected logger = new Logger('StorageService');

  constructor(private secureStorage: SecureStorageEcho, private platform: Platform) {}

  private async initStorage(): Promise<SecureStorageEchoObject | null> {
    if (this.storagePromise) {
      return this.storagePromise;
    }
    await this.platform.ready();
    if (this.platform.is('hybrid')) {
        this.storagePromise = this.secureStorage.create('mnlo_storage')
            .then(storage => {
              this.useFallbackLocalStorage = false;
              return storage;
            })
            .catch(err => {
              this.logger.warn('SecureStorage not available, falling back to localStorage', err);
              this.useFallbackLocalStorage = true;
              return null;
            });
    } else {
        this.useFallbackLocalStorage = true;
        this.storagePromise = Promise.resolve(null);
    }
    return this.storagePromise;
  }

  private isLocalStorageAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  async set(key: string, value: string): Promise<void> {
    const storage = await this.initStorage();
    const fallback = () => {
      if (this.useFallbackLocalStorage && this.isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
    } else {
      this.fallbackMemory[key] = value;
    }
  }
    if (storage) {
      try {
        // try to set directly (works on android and if key is new but ios throws an error)
        await storage.set(key, value);
      } catch (error: any) {
        // iOS duplicate error → remove first, then set again
        if (error?.error?.includes("already exists")) {
          await storage.remove(key);
          await storage.set(key, value);
        } else {
          this.logger.warn(error);
          // throw error; // rethrow other errors
          fallback();
        }
      }
    } else {
      fallback();
    }
  }

  async get(key: string): Promise<string | null> {
    const storage = await this.initStorage();
    if (storage) {
      try {
        return await storage.get(key);
      } catch {
        return null;
      }
    } else if (this.useFallbackLocalStorage && this.isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    } else {
      return this.fallbackMemory[key] || null;
    }
  }

  async remove(key: string): Promise<void> {
    const storage = await this.initStorage();
    if (storage) {
      try {
        await storage.remove(key);
      } catch {
        // ignore if key does not exist
      }
    } else if (this.useFallbackLocalStorage && this.isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    } else {
      delete this.fallbackMemory[key];
    }
  }
}
